import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useRef } from "react";

import { getSupabaseClient } from "../providers/supabase/supabase";

// Payload of the dashboard-api GET /config/h3-fleet endpoint (echoflicks-infra):
// the H3 GPU fleet this environment can render on — self-hosted workers pulling
// from the pool queue (registry rows in the H3 work bucket, freshness judged by
// S3 LastModified), the pool queue's depth and the RunPod Serverless endpoint's
// health. Reached through the session-gated /api/dashboard proxy. Shown on the
// GPU Fleet page and, in brief, on the AI Settings fleet card.
//
// Multi-family (plan 2026-10-04 TKT-I09): GET /config/gpu-fleet answers the same
// H3 payload plus `families`, keyed by model family (`h3`, `ltx25` — self-hosted
// LTX-2.5, the real-estate lane). The CRM asks for /config/gpu-fleet first and
// falls back to /config/h3-fleet when an older dashboard-api answers 404.

export type SelfHostedWorker = {
  worker_id: string;
  plane: string | null;
  location: string | null;
  state: string;
  current_job_id: string | null;
  gpu: string | null;
  vram_gib: number | null;
  profile: string | null;
  image_digest: string | null;
  comfy_commit: string | null;
  started_at: string | null;
  jobs_completed: number | null;
  jobs_failed: number | null;
  consecutive_failures: number | null;
  cert_not_after: string | null;
  host: string | null;
  last_seen_seconds_ago: number | null;
  fresh: boolean;
};

// Depth of the self-hosted pool queue: clips no worker has pulled yet, clips
// being rendered (in flight) and poison envelopes (DLQ); null when not bound.
export type PoolQueue = {
  queue: string;
  visible: number;
  in_flight: number;
  delayed: number;
  dlq: number | null;
};

// One RunPod pod, from RunPod's GraphQL API (not /health, whose counts lag and
// can be wrong): what image it runs, where, and whether it is doing anything.
export type RunPodPod = {
  id: string;
  status: string;
  image_digest: string | null;
  image_current: boolean;
  gpu: string | null;
  location: string | null;
  cost_per_hour: number | null;
  uptime_seconds: number | null;
  gpu_util_percent: number | null;
  memory_util_percent: number | null;
  last_change: string | null;
  since: string | null;
};

// One H3 clip as the composer's supervisor saw it.
export type H3Job = {
  job_id: string;
  /** The Echo (video session) the clip belongs to; its CRM page shows the delivered film. */
  workload_id?: string | null;
  /** The scene's number as text; an older API passes the composer's integer through. */
  scene_id?: string | number | null;
  status: "in_flight" | "completed" | "failed" | "unsupervised" | "unknown";
  /** False when the last dispatch went out on the v1 envelope: RunPod, not the supervisor, tracks it. */
  supervised?: boolean;
  last_event: string | null;
  last_reason: string | null;
  plane: string | null;
  worker_id: string | null;
  attempt: number | null;
  redispatched: number;
  lost: number;
  dispatched_at: string;
  last_event_at: string;
  /** When the supervisor logged the outcome; null while the clip is in flight. */
  ended_at?: string | null;
  age_seconds: number;
  /** Dispatch to first claim, when both events were seen (live slice only). */
  claim_wait_seconds?: number | null;
  gen_seconds: number | null;
  gpu: string | null;
};

export type H3PlaneTotals = {
  jobs: number;
  completed: number;
  failed: number;
  in_flight: number;
  /** Clips whose last dispatch was a v1 (unsupervised) envelope. */
  unsupervised?: number;
  redispatched: number;
  lost: number;
  avg_gen_seconds: number | null;
};

/** Clips finished in one time bucket of the history, by where they rendered. */
export type H3HistoryBucket = {
  start: string;
  self_hosted: number;
  runpod: number;
  failed: number;
};

/** What one worker did in the history window. `worker_id` is null for clips that failed before any claim. */
export type H3WorkerHistory = {
  worker_id: string | null;
  plane: string;
  gpu: string | null;
  clips: number;
  completed: number;
  failed: number;
  gen_seconds_total: number;
  avg_gen_seconds: number | null;
  p50_gen_seconds: number | null;
  max_gen_seconds: number | null;
  last_clip_at: string | null;
  /** Summed generation time over the window, 0..1: how busy the GPU was with H3. */
  busy_share: number;
};

// The history the page filters over (`?window_hours=`): clips that finished in
// the window, from the supervisor's terminal events only. `error` is set (and
// the rest absent) when that query failed; the live slice is still served.
export type H3History = {
  window_hours: number;
  bucket_seconds?: number;
  total?: number;
  completed?: number;
  failed?: number;
  series?: H3HistoryBucket[];
  by_plane?: Record<string, H3PlaneTotals>;
  by_worker?: H3WorkerHistory[];
  /** The newest clips of the window; the rest is only in the aggregates. */
  clips?: H3Job[];
  error?: string;
};

export type H3Jobs = {
  /** The live slice (every event of the last hours) behind `in_flight` and `recent`. */
  window_hours: number;
  in_flight: H3Job[];
  recent: H3Job[];
  by_plane: Record<string, H3PlaneTotals>;
  total: number;
  log_group?: string;
  /** Absent on an older dashboard-api. */
  history?: H3History | null;
};

/**
 * One owned GPU judged by the shared `h3_worker_eligibility` contract — the same
 * rule the composer and the render admission gate use — so "online" and
 * "schedulable" mean the same thing on every surface.
 */
export type RenderCapacityWorker = {
  worker_id: string;
  state: string;
  online: boolean;
  /** Counted as schedulable capacity by the scheduler. */
  capacity: boolean;
  available_now: boolean;
  /** Why it is not capacity (empty when it is). */
  reasons: string[];
  yielded_to_cotenant: boolean;
};

/** GET /config/h3-fleet `render_capacity` (plan 2026-09-26 H3Q-10); absent on an older dashboard-api. */
export type RenderCapacity = {
  workers: RenderCapacityWorker[];
  online: number;
  capacity: number;
  available_now: number;
  not_schedulable: Record<string, string[]>;
  policy: {
    echo_gpu_slots: number | null;
    local_gpu_slots_per_echo: number | null;
    runpod_max_workers: number | null;
    local_first: boolean;
  };
  release: { release_id: string | null; digest: string | null } | null;
  demand: {
    as_of: string;
    waiting: number;
    admitted: number;
    needs_operator: number;
    oldest_wait_seconds: number;
    deadline_at_risk: number;
    stale_waiters: number;
  } | null;
  /** RunPod's own readback of the endpoint (the scale-to-zero floor). */
  runpod_endpoint: {
    workers_min: number | null;
    workers_max: number | null;
    idle_timeout_seconds: number | null;
    gpu_type_ids: string[] | null;
    scaler: string | null;
  } | null;
  /** RunPod's billing record for the endpoint since 00:00 UTC. */
  runpod_billing_today: {
    day: string;
    usd: number;
    worker_hours: number;
  } | null;
};

/**
 * One model family's fleet inside GET /config/gpu-fleet `families`. Assumed to
 * reuse the H3 payload's blocks (every one optional): LTX-2.5 starts RunPod-only
 * (plan D3), so `self_hosted` may be absent or empty.
 */
export type GpuFamilyFleet = {
  work_bucket?: string | null;
  self_hosted?: H3Fleet["self_hosted"] | null;
  runpod?: H3Fleet["runpod"];
  pool_queue?: PoolQueue | null;
  jobs?: H3Jobs | null;
  render_capacity?: RenderCapacity | null;
  notes: string[];
};

/** Model-family labels (contracts §1: H3 = `h3`, LTX-2.5 = `ltx25`). */
export const GPU_FAMILY_LABELS: Record<string, string> = {
  h3: "H3",
  ltx25: "LTX-2.5",
};

export type H3Fleet = {
  environment: string;
  /** Per-family fleets from /config/gpu-fleet; absent on /config/h3-fleet. */
  families?: Record<string, GpuFamilyFleet>;
  generated_at?: string;
  /** When the dashboard-api read the fleet. */
  as_of?: string;
  render_capacity?: RenderCapacity | null;
  work_bucket: string;
  self_hosted: {
    workers: SelfHostedWorker[];
    counts: Record<string, number>;
    capacity: number;
  };
  runpod: {
    endpoint_id: string;
    workers: Record<string, number>;
    jobs: Record<string, number>;
    template_image?: string | null;
    pods?: RunPodPod[];
  } | null;
  pool_queue?: PoolQueue | null;
  jobs?: H3Jobs | null;
  notes: string[];
};

/**
 * Accepts every shape the dashboard-api serves:
 * - `/config/h3-fleet`: the H3 payload at the top level;
 * - `/config/gpu-fleet`: `{families, descriptors}`, where `families.h3` is the
 *   `/config/h3-fleet` payload verbatim (infra `services/dashboard-api/gpu_fleet.py`);
 * - the H3 payload at the top level plus `families`.
 */
export function normalizeH3Fleet(value: unknown): H3Fleet | null {
  if (!isRecord(value)) return null;
  const families = isRecord(value.families) ? value.families : undefined;
  const h3 = isH3Payload(value)
    ? value
    : families && isH3Payload(families.h3)
      ? families.h3
      : null;
  if (!h3) return null;
  // A missing or malformed `families` block reads as the H3-only payload.
  return {
    ...(h3 as unknown as H3Fleet),
    families: normalizeFamilies(families) ?? undefined,
  };
}

function isH3Payload(value: unknown): value is Record<string, unknown> {
  return (
    isRecord(value) &&
    typeof value.environment === "string" &&
    isRecord(value.self_hosted) &&
    Array.isArray(value.self_hosted.workers)
  );
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

/** Keep only well-formed families; a malformed block never hides the H3 fleet. */
function normalizeFamilies(
  value: unknown,
): Record<string, GpuFamilyFleet> | null {
  if (!isRecord(value)) return null;
  const families: Record<string, GpuFamilyFleet> = {};
  for (const [id, raw] of Object.entries(value)) {
    if (!isRecord(raw)) continue;
    const selfHosted =
      isRecord(raw.self_hosted) && Array.isArray(raw.self_hosted.workers)
        ? (raw.self_hosted as unknown as H3Fleet["self_hosted"])
        : null;
    const runpod =
      isRecord(raw.runpod) &&
      isRecord(raw.runpod.workers) &&
      isRecord(raw.runpod.jobs)
        ? (raw.runpod as unknown as NonNullable<H3Fleet["runpod"]>)
        : null;
    families[id] = {
      ...(raw as Partial<GpuFamilyFleet>),
      self_hosted: selfHosted,
      runpod,
      jobs: isRecord(raw.jobs) ? (raw.jobs as unknown as H3Jobs) : null,
      notes: Array.isArray(raw.notes)
        ? raw.notes.filter((note): note is string => typeof note === "string")
        : [],
    };
  }
  return Object.keys(families).length ? families : null;
}

/** The history window the API serves when none is asked for; the page starts here. */
export const DEFAULT_WINDOW_HOURS = 24;

/** The multi-family route, and the H3-only route an older dashboard-api serves. */
export type FleetRoute = "gpu-fleet" | "h3-fleet";

/**
 * `?window_hours=` selects the history (omitted for the default, so an older
 * dashboard-api sees the request it always saw); `?refresh=1` bypasses the API's
 * 30 s cache and is only sent for an operator's explicit click.
 */
export function fleetUrl({
  windowHours = DEFAULT_WINDOW_HOURS,
  refresh = false,
  route = "gpu-fleet",
}: {
  windowHours?: number;
  refresh?: boolean;
  route?: FleetRoute;
} = {}): string {
  const params = new URLSearchParams();
  if (windowHours !== DEFAULT_WINDOW_HOURS) {
    params.set("window_hours", String(windowHours));
  }
  if (refresh) params.set("refresh", "1");
  const query = params.toString();
  return `/api/dashboard/config/${route}${query ? `?${query}` : ""}`;
}

/**
 * After a 404 on /config/gpu-fleet (an older dashboard-api), polls go straight
 * to /config/h3-fleet until this time, then probe the new route again so a
 * dashboard-api deploy is picked up without a reload.
 */
const LEGACY_ROUTE_RECHECK_MS = 10 * 60 * 1000;
let legacyRouteUntil = 0;

/** Tests only: forget that the new route was missing. */
export function resetFleetRouteMemo() {
  legacyRouteUntil = 0;
}

async function fetchH3Fleet(options: {
  windowHours: number;
  refresh: boolean;
}): Promise<H3Fleet> {
  let token: string | undefined;
  try {
    const { data } = await getSupabaseClient().auth.getSession();
    token = data.session?.access_token;
  } catch {
    // No session -> the proxy will 401 and the page renders without the card.
  }
  const headers: Record<string, string> = token
    ? { Authorization: `Bearer ${token}` }
    : {};
  let route: FleetRoute =
    Date.now() < legacyRouteUntil ? "h3-fleet" : "gpu-fleet";
  let response = await fetch(fleetUrl({ ...options, route }), { headers });
  if (route === "gpu-fleet" && response.status === 404) {
    legacyRouteUntil = Date.now() + LEGACY_ROUTE_RECHECK_MS;
    route = "h3-fleet";
    response = await fetch(fleetUrl({ ...options, route }), { headers });
  }
  if (!response.ok) {
    throw new Error(`config/${route} responded ${response.status}`);
  }
  const fleet = normalizeH3Fleet(await response.json());
  if (fleet) return fleet;
  if (route === "gpu-fleet") {
    // A shape this client cannot read must not blank the page while the
    // H3-only route still answers (2026-10-10: the page went dark on a
    // contract drift). Not remembered, unlike the 404: the next poll tries
    // /config/gpu-fleet again, so a fixed API is picked up without a reload.
    console.warn(
      "config/gpu-fleet returned an unexpected shape; reading config/h3-fleet",
    );
    const legacy = await fetch(fleetUrl({ ...options, route: "h3-fleet" }), {
      headers,
    });
    const legacyFleet = legacy.ok
      ? normalizeH3Fleet(await legacy.json())
      : null;
    if (legacyFleet) return legacyFleet;
  }
  throw new Error(`config/${route} returned an unexpected shape`);
}

export type H3FleetQuery = {
  /** The fleet, or null when the endpoint is unavailable (older dashboard-api, no bucket binding, no session). */
  fleet: H3Fleet | null;
  isLoading: boolean;
  isFetching: boolean;
  error: Error | null;
  /** True while the shown fleet is the previous window's, kept on screen until the new one lands. */
  isPreviousWindow: boolean;
  /** Epoch ms of the last successful fetch, 0 before the first. */
  dataUpdatedAt: number;
  /** Operator refresh: bypasses the API's cache. */
  refetch: () => void;
};

// One query per history window, shared by every surface that shows the fleet;
// React Query polls at the shortest interval any mounted surface asks for.
export function useH3Fleet(
  options: { refetchIntervalMs?: number; windowHours?: number } = {},
): H3FleetQuery {
  const windowHours = options.windowHours ?? DEFAULT_WINDOW_HOURS;
  const forceRefresh = useRef(false);
  const query = useQuery({
    queryKey: ["ai-h3-fleet", windowHours],
    queryFn: async () => {
      const refresh = forceRefresh.current;
      forceRefresh.current = false;
      return fetchH3Fleet({ windowHours, refresh });
    },
    staleTime: 30 * 1000,
    refetchInterval: options.refetchIntervalMs ?? 60 * 1000,
    // Switching the window keeps the page on screen until the new one lands.
    placeholderData: keepPreviousData,
    retry: false,
  });
  return {
    fleet: query.data ?? null,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    isPreviousWindow: query.isPlaceholderData,
    error: query.error instanceof Error ? query.error : null,
    dataUpdatedAt: query.dataUpdatedAt,
    refetch: () => {
      forceRefresh.current = true;
      void query.refetch();
    },
  };
}

// Whether a self-hosted worker runs the image the RunPod template runs. The
// registry row carries the worker's digest; the reference digest is whatever the
// operator pinned, passed in by the page (unknown → no verdict).
export function imageDriftLabel(
  worker: SelfHostedWorker,
  referenceDigest: string | null,
): "same" | "drift" | "unknown" {
  if (!worker.image_digest || !referenceDigest) return "unknown";
  return worker.image_digest.startsWith(referenceDigest.slice(0, 19))
    ? "same"
    : "drift";
}

export function describeWorkerState(state: string): string {
  switch (state) {
    case "idle":
      return "idle — ready for a clip";
    case "busy":
      return "rendering";
    case "draining":
      return "paused (maintenance)";
    case "blocked_gpu_busy":
      return "GPU in use by another process";
    case "quarantined":
      return "quarantined after repeated failures";
    case "stale":
      return "no heartbeat — offline?";
    case "offline":
      return "stopped (compose down or drained)";
    default:
      return state;
  }
}
